/* eslint-disable indent */
import { isUserInSessionOrRoleSuperAdmin } from '@src/main/modules/auth/authUtils';
import { createExportHandlers } from '@src/main/modules/messages/utils/createExportHandlers';
import { normalizeRole } from '@src/main/modules/messages/utils/formatters';
import { CreateUpdateRoleRequest } from '@src/main/modules/roles/infrastructure/dto/createUpdateUserRequest';
import RoleForm from '@src/main/modules/roles/ui/components/RoleForm';
import { useCreateRole } from '@src/main/modules/roles/ui/hooks/useCreateRole';
import { useDeleteRole } from '@src/main/modules/roles/ui/hooks/useDeleteRole';
import { usePermissions } from '@src/main/modules/roles/ui/hooks/usePermissions';
import { useRoles } from '@src/main/modules/roles/ui/hooks/useRoles';
import { useUpdateRole } from '@src/main/modules/roles/ui/hooks/useUpdateRole';
import { RoleTableRow } from '@src/main/modules/roles/ui/role-table-row.model';
import { getRoleSchema } from '@src/main/modules/roles/ui/role.schema';
import { PERMISSIONS } from '@src/main/shared/constants/permissions';
import { useAuth } from '@src/main/shared/context/AuthContext';
import { useToast } from '@src/main/shared/context/ToastContext';
import ContentCard from '@src/main/shared/ui/components/card/ContentCard';
import AppDialog from '@src/main/shared/ui/components/dialog/AppDialog';
import ConfirmDeleteDialog from '@src/main/shared/ui/components/dialog/ConfirmDeleteDialog';
import HeaderTitle from '@src/main/shared/ui/components/header/HeaderTitle';
import AppLoading from '@src/main/shared/ui/components/spinner/AppLoading';
import AppTable from '@src/main/shared/ui/components/table/AppTable';
import TableHeader from '@src/main/shared/ui/components/table/TableHeader';
import { formatList } from '@src/main/shared/utils/formatList';
import { showErrorMessage, showSuccessMessage } from '@src/main/shared/utils/toastUtils';
import { useQueryClient } from '@tanstack/react-query';
import { CheckboxChangeEvent } from 'primereact/checkbox';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';

const RolesManagement: FC = () => {
  const { t } = useTranslation();
  const { hasPermission, user: currentUser } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();
  const { data = [], isLoading: loadingRoles, repository } = useRoles();
  const { data: permissions = [], isLoading: loadingPermissions } = usePermissions();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [newEditRole, setNewEditRole] = useState<CreateUpdateRoleRequest>({
    role: '',
    description: '',
    permissions: [],
  });
  const hasEditPermissions = hasPermission(PERMISSIONS.ROLES_MANAGEMENT.edit);
  const [selectedRoles, setSelectedRoles] = useState<RoleTableRow[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState<RoleTableRow | null>(null);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const toolbar = [false, hasEditPermissions, selectedRoles.length > 0, true, false, false];

  const rolesData: RoleTableRow[] = data.map((role) => ({
    id: role.roleId!,
    ...role,
  }));

  const FILE_NAME = t('rolesManagement.fileName');
  const { onExportExcel, onExportPdf } = createExportHandlers(
    rolesData,
    normalizeRole,
    FILE_NAME,
    t,
  );

  const { mutateAsync: createRole } = useCreateRole();
  const { mutateAsync: updateRole } = useUpdateRole();
  const { mutateAsync: deleteRole } = useDeleteRole();

  const handleShowCreateRole = () => {
    setNewEditRole({
      role: '',
      description: '',
      permissions: [],
    });
    setIsEditMode(false);
    setIsModalVisible(true);
  };

  const handleEditRole = (row: RoleTableRow) => {
    const role: CreateUpdateRoleRequest = {
      role: row.role,
      description: row.description,
      permissions: row.permissions.map((p) => p.permissionId),
    };
    setNewEditRole(role);
    setEditingRoleId(row.id);
    setIsEditMode(true);
    setIsModalVisible(true);
  };

  const handleCreateEdit = async () => {
    if (!repository) return;
    const schema = getRoleSchema(t);

    try {
      schema.parse(newEditRole);

      if (isEditMode) {
        const updateRequest: CreateUpdateRoleRequest = {
          ...newEditRole,
        };
        if (!editingRoleId) {
          throw new Error('No role selected for editing.');
        }
        await updateRole({ id: editingRoleId, data: updateRequest });
        showSuccessMessage(toast, t('rolesManagement.updatedSuccess'));
      } else {
        await createRole(newEditRole);
        showSuccessMessage(toast, t('rolesManagement.createdSuccess'));
      }

      await queryClient.invalidateQueries({ queryKey: ['role'] });
      setIsModalVisible(false);
      setEditingRoleId(null);
      setValidationErrors({});
    } catch (error) {
      if (error instanceof z.ZodError) {
        const errors: Record<string, string> = {};
        error.errors.forEach((err) => {
          if (err.path.length > 0) {
            const key = err.path[0];
            errors[key] = err.message;
          }
        });
        setValidationErrors(errors);
      } else {
        showErrorMessage(toast, error);
      }
    }
  };

  const handlePermissionChange = (permissionId: string, e: CheckboxChangeEvent) => {
    const updatedPermissions = e.checked
      ? [...new Set([...newEditRole.permissions, permissionId])]
      : newEditRole.permissions.filter((p) => p !== permissionId);

    setNewEditRole({ ...newEditRole, permissions: updatedPermissions });
  };

  const handleDeleteRole = (role: RoleTableRow) => {
    setRoleToDelete(role);
  };

  const confirmDelete = async () => {
    if (!repository) return;
    try {
      if (isBulkDelete) {
        await Promise.all(selectedRoles.map((role) => deleteRole(role.roleId!)));
        setSelectedRoles([]);
      } else if (roleToDelete) {
        await deleteRole(roleToDelete.roleId!);
        setSelectedRoles((prev) => prev.filter((u) => u.role !== roleToDelete.role));
      }

      await queryClient.invalidateQueries({ queryKey: ['role'] });
      showSuccessMessage(toast, t('rolesManagement.deletedSuccess'));
    } catch (error) {
      showErrorMessage(toast, error);
    } finally {
      setRoleToDelete(null);
      setIsBulkDelete(false);
    }
  };

  const rejectDelete = () => {
    setRoleToDelete(null);
    setIsBulkDelete(false);
  };

  const deleteSelectedRoles = () => {
    if (selectedRoles.length === 0) return;
    setIsBulkDelete(true);
  };

  return (
    <ContentCard>
      <HeaderTitle title={t('rolesManagement.title')} />
      <TableHeader
        onCreate={handleShowCreateRole}
        onDelete={deleteSelectedRoles}
        onRefresh={async () => {
          setIsRefreshing(true);
          await queryClient.invalidateQueries({ queryKey: ['role'] });
          setIsRefreshing(false);
        }}
        onExportExcel={onExportExcel}
        onExportPdf={onExportPdf}
        visibility={toolbar}
        exportable
      />
      <AppLoading isLoading={loadingRoles || isRefreshing}>
        <AppTable
          headers={[
            { name: t('rolesManagement.role'), field: 'role' },
            { name: t('rolesManagement.description'), field: 'description' },
          ]}
          data={rolesData}
          onEdit={hasEditPermissions ? handleEditRole : undefined}
          onDelete={hasEditPermissions ? handleDeleteRole : undefined}
          onSelectedChange={(rows) => {
            const filtered = rows.filter((r) => !isUserInSessionOrRoleSuperAdmin(r, currentUser));
            setSelectedRoles(filtered);
          }}
          disableActions={(row) => isUserInSessionOrRoleSuperAdmin({ role: row.role }, currentUser)}
        />
        <AppDialog
          visible={isModalVisible}
          title={
            isEditMode
              ? t('rolesManagement.updateRole', {
                  role: newEditRole.role,
                })
              : t('rolesManagement.createRole')
          }
          onClose={() => setIsModalVisible(false)}
          disableButtons={loadingRoles}
          onConfirm={handleCreateEdit}
        >
          <AppLoading isLoading={loadingPermissions}>
            <RoleForm
              values={newEditRole}
              availablePermissions={permissions}
              handleChange={(field, value) => setNewEditRole({ ...newEditRole, [field]: value })}
              handlePermissionChange={handlePermissionChange}
              errors={validationErrors}
            />
          </AppLoading>
        </AppDialog>
        <ConfirmDeleteDialog
          confirmDelete={confirmDelete}
          rejectDelete={rejectDelete}
          visible={isBulkDelete || roleToDelete !== null}
          message={
            isBulkDelete
              ? t('rolesManagement.confirmBulkDelete', {
                  roles: formatList(
                    selectedRoles.map((r) => r.role),
                    t('common.and'),
                  ),
                })
              : t('rolesManagement.confirmDelete', {
                  role: roleToDelete?.role,
                })
          }
        />
      </AppLoading>
    </ContentCard>
  );
};

export default RolesManagement;
